import { createAction, createAsyncThunk, createListenerMiddleware, createSelector, createSlice, TypedStartListening } from '@reduxjs/toolkit';
import { definitions } from '@/apitypes';
import { CalculatePriceQuery, RebateCodePrice, RebateGetBySignResponse, Time, TotalPriceInformationResponse } from '@/types';
import { AxiosResponse } from 'axios';
import { apiClient, throwSubmissionError } from '@/utils';
import { reset as resetForm } from 'redux-form';
import { ApplicationDispatch, ApplicationState } from '@/store';
import Notifications from 'react-notification-system-redux';
import { getTranslate } from 'react-localize-redux';
import { toDate } from '@/utils/datetime';
import { parseIntervalKey } from '@/utils/common';
import { DATE_QUANTITIES_PREFIX } from '@/containers/Booking/NewBooking/DateQuantities';

export interface PartialQuantity {
    Quantity?: number
    PriceId?: number
    Interval?: Time
}
export type CalculatedPriceEntityElement = TotalPriceInformationResponse & PartialQuantity;
interface State {
    entity?: {
        calculatedPrice?: CalculatedPriceEntityElement[];
        appliedCodes?: RebateCodePrice[];
    };
    loading: boolean;
    error: any;
}

const initialState: State = {
    entity: undefined,
    loading: false,
    error: null,
};



/** Service add-on articles, in the exact shape the API expects. */
export interface CalculatePriceArticle {
    ArticleId: number;
    Quantity: number;
}

/**
 * Stands in for the `PriceId` of a quote that priced only add-ons, i.e. one sent
 * with no `Quantities` at all. Entries are addressed by `PriceId + Interval`, so
 * without a stable id such a quote could never be removed again and its total
 * would linger after the add-ons were taken off.
 */
export const ARTICLE_ONLY_PRICE_ID = -1;

interface CalculatePriceArgument {
    interval: Time,
    quantities: { Quantity: number, PriceId: number }[],
    customerEmail?: string,
    serviceId: number,
    rebateCodeIds?: number[],
    articles?: CalculatePriceArticle[]
}

/**
 * Reports a failed price preview to the admin.
 *
 * This deliberately does NOT call `rejectWithValue`: returning it from here would
 * type the helper as `any`, which collapses the payload creator's inferred return
 * type and erases the `fulfilled` payload type in the reducers below. Each caller
 * returns `thunkApi.rejectWithValue(...)` itself, the way `applyRebateCode` does.
 */
function notifyCalculatePriceFailure(dispatch: (action: any) => void, error: any) {
    dispatch(
        Notifications.show(
            { title: error?.response?.data?.ResponseStatus?.Message as string },
            'error'
        )
    );
}

export const calculatePriceListener = createListenerMiddleware();
export type CalculatePriceStartListening = TypedStartListening<ApplicationState, ApplicationDispatch>;
const startCalculatePriceListener = calculatePriceListener.startListening as CalculatePriceStartListening;

// Thunks

export const applyRebateCode = createAsyncThunk(
    '/services/calculatePrice/APPLY_REBATE_CODE',
    async ({
        rebateCodeSign,
        interval,
        quantities = [],
        customerEmail,
        serviceId,
        articles,
    } : {
        rebateCodeSign: string,
        interval: Time,
        quantities: { Quantity: number; PriceId: number }[],
        customerEmail?: string,
        serviceId: number,
        articles?: CalculatePriceArticle[]
    }, thunkApi) => {
        try {
            const state = thunkApi.getState() as ApplicationState;
            const translate = getTranslate(state.localize);
            const companyId = state.company.data?.Id;

            const alreadyAppliedCode =
                state.calculatePrice.entity?.calculatedPrice &&
                Object.values((state.calculatePrice.entity.calculatedPrice[0] as any).AppliedCodes).some((p: any) => 
                    p.RebateCodeSign === rebateCodeSign
                );
            
            if(alreadyAppliedCode) {
                return thunkApi.rejectWithValue(throwSubmissionError({
                    _error: translate('newBookingForm.codeAlreadyApplied')
                }));
            }

            const getbysignResponse: AxiosResponse<RebateGetBySignResponse> = await apiClient.get('/rebatecodes/getbysign', {
                params: {
                    RebateCodeSign: rebateCodeSign,
                    Date: toDate(new Date(interval.From)).toISOString(),
                    CustomerEmail: customerEmail,
                    ServiceId: serviceId,
                    CompanyId: companyId,
                },
            });

            const calculatePriceResponse: AxiosResponse<
                definitions['TotalPriceInformationResponse']
            > = await apiClient.put(`/services/${serviceId}/calculateprice`, {
                Id: serviceId,
                Interval: interval,
                Quantities: quantities || [],
                RebateCodeIds: [getbysignResponse.data.Id],
                CustomerEmail: customerEmail,
                // Once a code is applied the displayed total switches to
                // appliedCodes[0].TotalPrice, so omitting the add-ons here would
                // silently drop them from what the admin sees.
                ...(articles?.length ? { Articles: articles } : {}),
            });

            return thunkApi.fulfillWithValue(calculatePriceResponse.data as TotalPriceInformationResponse);
        } catch (error: any) {
            thunkApi.dispatch(
                Notifications.show(
                    { title: error.response?.data?.ResponseStatus?.Message as string },
                    'error'
                )
            );
            return thunkApi.rejectWithValue(throwSubmissionError(error));
        }
    }
);

export const removeRebateCode = createAsyncThunk(
    '/services/calculatePrice/REMOVE_REBATE_CODE',
    async ({
        rebateCodeSign,
        interval,
        quantities = [],
        customerEmail,
        serviceId,
        articles
    } : {
        rebateCodeSign: string,
        interval: Time,
        quantities: { Quantity: number; PriceId: number }[],
        customerEmail: string,
        serviceId: number,
        articles?: CalculatePriceArticle[]
    }, thunkApi) => {
        try {
            const calculatePriceResponse: AxiosResponse<
                definitions['TotalPriceInformationResponse']
            > = await apiClient.put(`/services/${serviceId}/calculateprice`, {
                Id: serviceId,
                Interval: interval,
                Quantities: quantities || [],
                RebateCodeIds: [],
                CustomerEmail: customerEmail,
                ...(articles?.length ? { Articles: articles } : {}),
            });

            return thunkApi.fulfillWithValue(calculatePriceResponse.data as TotalPriceInformationResponse);
        } catch (error: any) {
            thunkApi.dispatch(
                Notifications.show(
                    { title: error.response?.data?.ResponseStatus?.Message as string },
                    'error'
                )
            );
            return thunkApi.rejectWithValue(throwSubmissionError(error));
        }
    }
);

export const calculatePrice = createAsyncThunk(
    '/services/calculatePrice/CALCULATE_PRICE',
    async ({
        interval,
        quantities = [],
        customerEmail,
        serviceId,
        rebateCodeIds = [],
        articles
    } : CalculatePriceArgument, thunkApi) => {
        try {
            const calculatePriceResponse: AxiosResponse<
                definitions['TotalPriceInformationResponse']
            > = await apiClient.put(`/services/${serviceId}/calculateprice`, {
                Id: serviceId,
                Interval: interval,
                Quantities: quantities || [],
                CustomerEmail: customerEmail,
                RebateCodeIds: rebateCodeIds,
                ...(articles?.length ? { Articles: articles } : {})
            } as CalculatePriceQuery);

            return thunkApi.fulfillWithValue({
                ...calculatePriceResponse.data,
                ...quantities[0],
                PriceId: quantities[0]?.PriceId ?? ARTICLE_ONLY_PRICE_ID,
                Interval: interval
            } as CalculatedPriceEntityElement);
        } catch (error: any) {
            // Without this the rejection is silent: `action.payload` would be
            // undefined, so `state.error` stayed falsy, the submit button's
            // `disabled={calculatedPrice.error}` guard never fired, and the admin
            // was left looking at a stale total with no message.
            notifyCalculatePriceFailure(thunkApi.dispatch, error);
            return thunkApi.rejectWithValue(throwSubmissionError(error));
        }
})

export const calculatePrices = createAsyncThunk(
    '/services/calculatePrice/CALCULATE_PRICES',
    async (priceDates : CalculatePriceArgument[], thunkApi) => {
        try {
            const response = await Promise.all(priceDates.map(async priceDate => {
                const data = await apiClient.put(`/services/${priceDate.serviceId}/calculateprice`, {
                    Id: priceDate.serviceId,
                    Interval: priceDate.interval,
                    Quantities: priceDate.quantities || [],
                    CustomerEmail: priceDate.customerEmail,
                    RebateCodeIds: priceDate.rebateCodeIds,
                    ...(priceDate.articles?.length ? { Articles: priceDate.articles } : {})
                } as CalculatePriceQuery)

                return {
                    ...data.data,
                    ...priceDate.quantities[0],
                    PriceId: priceDate.quantities[0]?.PriceId ?? ARTICLE_ONLY_PRICE_ID,
                    Interval: priceDate.interval
                }
            }));

            return thunkApi.fulfillWithValue(response as CalculatedPriceEntityElement[]);
        } catch (error: any) {
            notifyCalculatePriceFailure(thunkApi.dispatch, error);
            return thunkApi.rejectWithValue(throwSubmissionError(error));
        }
    }
)

export const removeCalculatedPrice = createAction(
    '/services/calculatePrice/REMOVE_CALCULATED_PRICE',
    (payload: { PriceId: number; Interval: Time }) => ({ payload })
);

export const reset = createAction('/services/calculatePrice/RESET')

// Slice

const calcualtePriceSlice = createSlice({
    name: 'calculatePrice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(calculatePrices.pending, (state, action) => {
            state.loading = true;
        });

        builder.addCase(calculatePrices.fulfilled, (state, action) => {
            const newPrices = action.payload;
            const currentPrices = state.entity?.calculatedPrice || [];
            
            let nextPrices = [...currentPrices];
            newPrices.forEach(newPrice => {
                const existingIndex = nextPrices.findIndex(p => 
                    toDate(p.Interval?.From as string).toISOString() === toDate(newPrice.Interval?.From as string).toISOString() && 
                    toDate(p.Interval?.To as string).toISOString() === toDate(newPrice.Interval?.To as string).toISOString()
                );

                if (existingIndex >= 0) {
                    nextPrices[existingIndex] = newPrice;
                } else {
                    nextPrices.push(newPrice);
                }
            });

            state.entity = {
                ...state.entity,
                calculatedPrice: nextPrices
            };
            state.loading = false;
            state.error = null;
        });

        builder.addCase(calculatePrices.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
        });

        builder.addCase(calculatePrice.pending, (state, action) => {
            state.loading = true;
        });

        builder.addCase(calculatePrice.fulfilled, (state, action) => {
            const newPrice = action.payload;
            const currentPrices = state.entity?.calculatedPrice || [];
            const existingIndex = currentPrices.findIndex(p => 
                toDate(p.Interval?.From as string).toISOString() === toDate(newPrice.Interval?.From as string).toISOString() && 
                toDate(p.Interval?.To as string).toISOString() === toDate(newPrice.Interval?.To as string).toISOString()
            );

            let nextPrices = [...currentPrices];
            if (existingIndex >= 0) {
                nextPrices[existingIndex] = newPrice;
            } else {
                nextPrices.push(newPrice);
            }

            state.entity = {
                ...state.entity,
                calculatedPrice: nextPrices
            };
            state.loading = false;
            state.error = null;
        });

        builder.addCase(calculatePrice.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
        });

        builder.addCase(applyRebateCode.pending, (state, action) => {
            state.loading = true;
        });

        builder.addCase(applyRebateCode.fulfilled, (state, action) => {
            state.loading = false;
            if(state.entity) {
                state.entity.appliedCodes = [action.payload];
            }
        });

        builder.addCase(applyRebateCode.rejected, (state, action) => {
            state.loading = false;
        });


        builder.addCase(removeRebateCode.pending, (state, action) => {
            state.loading = true;
        });

        builder.addCase(removeRebateCode.fulfilled, (state, action) => {
            state.loading = false;
            if(state.entity) {
                state.entity.appliedCodes = undefined;
            }
        });

        builder.addCase(removeRebateCode.rejected, (state, action) => {
            state.loading = false;
        });

        builder.addCase(removeCalculatedPrice, (state, action) => {
            const { PriceId, Interval } = action.payload;
            if (state.entity?.calculatedPrice) {
                state.entity.calculatedPrice = state.entity.calculatedPrice.filter(p => 
                    !(p.PriceId === PriceId && 
                      toDate(p.Interval?.From as string).toISOString() === toDate(Interval?.From as string).toISOString() && 
                      toDate(p.Interval?.To as string).toISOString() === toDate(Interval?.To as string).toISOString())
                );
            }
        });

        builder.addCase(reset, (state) => {
            state.entity = initialState.entity;
            state.error = initialState.error;
            state.loading = initialState.loading;
        })

    },
});

startCalculatePriceListener({
    matcher: calculatePrice.fulfilled.match,
    effect: async (action, api) => {
        const state = api.getState();
        
        if(!state.calculatePrice.loading && state.calculatePrice.entity) {
            api.dispatch({
                type: 'UPDATE_SERVICE_QUANTITIES',
                payload: []
            })
        }
    }
});

// Listeners

startCalculatePriceListener({
    matcher: removeRebateCode.fulfilled.match,
    effect: async (_, api) => {
        api.dispatch(resetForm('promoCodes'));
    }
});

startCalculatePriceListener({
    matcher: calculatePrice.fulfilled.match,
    effect: async (action, api) => {
        const appliedCode = getAppliedCode(api.getState());
        
        if(appliedCode) {
            api.dispatch(applyRebateCode({
                rebateCodeSign: appliedCode.RebateCodeSign!,
                interval: action.meta.arg.interval,
                serviceId:  action.meta.arg.serviceId,
                customerEmail:  action.meta.arg.customerEmail,
                quantities:  action.meta.arg.quantities,
                // Must ride along: this re-apply is what produces the displayed
                // total while a code is active.
                articles: action.meta.arg.articles
            }))
        }
    }
});

startCalculatePriceListener({
    matcher: calculatePrices.fulfilled.match,
    effect: async (action, api) => {
        const appliedCode = getAppliedCode(api.getState());
        
        if(appliedCode) {
            api.dispatch(applyRebateCode({
                rebateCodeSign: appliedCode.RebateCodeSign!,
                interval: action.meta.arg[0].interval,
                serviceId:  action.meta.arg[0].serviceId,
                customerEmail:  action.meta.arg[0].customerEmail,
                // Articles are per-booking and identical across the series, so the
                // first entry's list is the whole list.
                articles: action.meta.arg[0].articles,
                quantities:  action.meta.arg.reduce((acc: Required<PartialQuantity>[], curr: any) => {
                    acc.push(...curr.quantities);
                    return acc;
                }, [] as Required<PartialQuantity>[])
            }))
        }
    }
});

// Selectors

const selectSelf = (state: ApplicationState) => state

export const getAppliedCodeIds = createSelector(selectSelf, (state) => {
    return state.calculatePrice.entity?.appliedCodes && state.calculatePrice.entity?.appliedCodes[0]?.AppliedCodes
        ? state.calculatePrice.entity.appliedCodes[0].AppliedCodes.map((appliedCode: any) => appliedCode.RebateCodeId) || []
        : [];
});

export const getAppliedCode = createSelector(selectSelf, (state) => {
    return state.calculatePrice.entity?.appliedCodes && state.calculatePrice.entity.appliedCodes[0]?.AppliedCodes
        ? state.calculatePrice.entity.appliedCodes[0].AppliedCodes[0]
        : undefined;
});

/**
 * Returns the total quantity for a given date interval, combining:
 * 1. The new dateQuantitiesForm slice (group booking path)
 * 2. Legacy redux-form entries (non-group booking path)
 *
 * NOTE: This is intentionally NOT a createSelector — the second arg factory
 * `(_, from, to) => [from, to]` created a new array each call, defeating
 * memoisation anyway. A plain function is cheaper and more honest.
 */
export const getTotalQuantityForDate = (state: any, from: string, to: string): number => {
    let total = 0;

    // 1. New slice (group path) — O(prices-in-interval), fast
    const intervalKey = `${new Date(from).toISOString()}|${new Date(to).toISOString()}`;
    const slicePrices = state.dateQuantitiesForm?.quantities?.[intervalKey];
    if (slicePrices) {
        const vals = Object.values(slicePrices) as number[];
        for (let i = 0; i < vals.length; i++) total += vals[i];
    }

    // 2. Legacy redux-form (non-group path) — only scans non-group forms (few)
    if (state.form) {
        const target = new Date(from).getTime();
        const entries = Object.entries(state.form as any);
        for (let i = 0; i < entries.length; i++) {
            const [key, value] = entries[i] as [string, any];
            if (!key.includes(DATE_QUANTITIES_PREFIX) || !value?.values) continue;
            const { From } = parseIntervalKey(key, DATE_QUANTITIES_PREFIX);
            const t = new Date(From).getTime();
            if (!Number.isNaN(t) && t === target) {
                total += value.values.quantity;
            }
        }
    }

    return total;
};

export default calcualtePriceSlice;
